// Inventory & Assets — Phase 2 tabs: Verification, Movement, Repairs.
// Kept as a separate file so InventoryAssets.jsx stays readable.

import { useCallback, useEffect, useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue }
  from "../../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow }
  from "../../components/ui/table";
import { Badge } from "../../components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter }
  from "../../components/ui/dialog";
import { toast } from "sonner";
import { Loader2, Plus, RefreshCw, ClipboardCheck, ArrowRightLeft, Wrench,
         CheckCircle2, XCircle } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL;

const post = async (path, token, body = {}) => {
  const r = await fetch(`${API}/api/inventory${path}`, {
    method: "POST",
    headers: { "Content-Type": "application/json" },
    body: JSON.stringify({ token, ...body }),
  });
  if (!r.ok) throw new Error((await r.json().catch(() => ({}))).detail || `HTTP ${r.status}`);
  return r.json();
};

const money = (v) => `₹${Number(v || 0).toLocaleString(undefined,
  { minimumFractionDigits: 0, maximumFractionDigits: 2 })}`;


/* ============================================================
 * Physical Verification
 * ============================================================ */
export function VerificationPanel({ token, center, categories, conditions, isAdmin }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [openId, setOpenId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await post("/verification/list", token, { center });
      setRows(d.verifications || []);
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [token, center]);
  useEffect(() => { load(); }, [load]);

  return (
    <Card data-testid="inv-verify-panel">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <ClipboardCheck className="w-4 h-4" /> Physical Verification
          </CardTitle>
          <div className="text-xs text-muted-foreground">
            Schedule a verification, count actuals, then admin signs off to update stock.
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => setCreating(true)} data-testid="inv-verify-new">
            <Plus className="w-4 h-4 mr-1" /> New Verification
          </Button>
          <Button variant="ghost" size="sm" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader><TableRow>
            <TableHead>ID</TableHead>
            <TableHead>Scheduled</TableHead>
            <TableHead>Centre</TableHead>
            <TableHead>Scope</TableHead>
            <TableHead>Cadence</TableHead>
            <TableHead>Status</TableHead>
            <TableHead className="text-right">Variance Value</TableHead>
            <TableHead>Signed By</TableHead>
            <TableHead className="text-right">Action</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map(v => (
              <TableRow key={v.verification_id} data-testid={`inv-verify-row-${v.verification_id}`}>
                <TableCell className="font-mono text-xs">{v.verification_id}</TableCell>
                <TableCell>{v.scheduled_date}</TableCell>
                <TableCell>{v.center}</TableCell>
                <TableCell>{v.category_code || "Whole centre"}</TableCell>
                <TableCell>{v.cadence || "—"}</TableCell>
                <TableCell>
                  <Badge variant={v.status === "Signed" ? "default"
                                : v.status === "Completed" ? "secondary" : "outline"}>
                    {v.status}
                  </Badge>
                </TableCell>
                <TableCell className={`text-right ${(v.variance_total_value || 0) < 0 ? "text-red-500" : ""}`}>
                  {v.variance_total_value ? money(v.variance_total_value) : "—"}
                </TableCell>
                <TableCell>{v.signed_by || "—"}</TableCell>
                <TableCell className="text-right">
                  <Button size="sm" variant="outline"
                    onClick={() => setOpenId(v.verification_id)}
                    data-testid={`inv-verify-open-${v.verification_id}`}>
                    {v.status === "Signed" ? "View" : "Open"}
                  </Button>
                </TableCell>
              </TableRow>
            ))}
            {!loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-6">
                No verifications yet. Click <b>New Verification</b> to schedule one.
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>

      {creating && <NewVerificationDialog token={token} center={center}
        categories={categories} onClose={() => { setCreating(false); load(); }} />}
      {openId && <VerificationSheet token={token} verificationId={openId}
        conditions={conditions} isAdmin={isAdmin}
        onClose={() => { setOpenId(null); load(); }} />}
    </Card>
  );
}


function NewVerificationDialog({ token, center, categories, onClose }) {
  const [f, setF] = useState({
    category_code: "", scheduled_date: new Date().toISOString().slice(0, 10),
    cadence: "Quarterly", remarks: "",
  });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await post("/verification/create", token, { center, ...f,
        category_code: f.category_code || null });
      toast.success("Verification scheduled");
      onClose();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="inv-verify-new-dialog">
        <DialogHeader><DialogTitle>Schedule Physical Verification</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Scheduled Date</Label>
            <Input type="date" value={f.scheduled_date}
              onChange={(e) => setF({ ...f, scheduled_date: e.target.value })} /></div>
          <div><Label>Cadence</Label>
            <Select value={f.cadence} onValueChange={(v) => setF({ ...f, cadence: v })}>
              <SelectTrigger><SelectValue /></SelectTrigger>
              <SelectContent>
                {["Monthly", "Quarterly", "Half-Yearly", "Annual", "Custom"].map(c =>
                  <SelectItem key={c} value={c}>{c}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label>Category (leave blank for whole centre)</Label>
            <Select value={f.category_code || "ALL"}
              onValueChange={(v) => setF({ ...f, category_code: v === "ALL" ? "" : v })}>
              <SelectTrigger data-testid="inv-verify-scope"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="ALL">Whole centre</SelectItem>
                {categories.filter(c => c.code !== "RAW_MATERIAL").map(c =>
                  <SelectItem key={c.code} value={c.code}>{c.letter}. {c.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="col-span-2"><Label>Remarks</Label>
            <Textarea rows={2} value={f.remarks}
              onChange={(e) => setF({ ...f, remarks: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="inv-verify-schedule">
            {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Schedule
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function VerificationSheet({ token, verificationId, conditions, isAdmin, onClose }) {
  const [ver, setVer] = useState(null);
  const [items, setItems] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    post("/verification/get", token, { verification_id: verificationId })
      .then(d => { setVer(d.verification); setItems(d.verification.items || []); })
      .catch(e => toast.error(e.message));
  }, [token, verificationId]);

  if (!ver) return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent><Loader2 className="w-5 h-5 animate-spin" /></DialogContent>
    </Dialog>
  );

  const readOnly = ver.status === "Signed";
  const setItem = (i, k, v) => setItems(items.map((r, idx) => idx === i ? { ...r, [k]: v } : r));

  const submit = async () => {
    setBusy(true);
    try {
      const payload = items.filter(r => r.actual_qty !== null && r.actual_qty !== "")
        .map(r => ({ asset_id: r.asset_id, actual_qty: Number(r.actual_qty),
                     condition_observed: r.condition_observed, remarks: r.remarks }));
      const r = await post("/verification/submit", token,
        { verification_id: verificationId, items: payload });
      toast.success(`Counted ${r.counted} / variance ${money(r.variance_total_value)}`);
      const d = await post("/verification/get", token, { verification_id: verificationId });
      setVer(d.verification); setItems(d.verification.items || []);
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  const signOff = async () => {
    if (!window.confirm("Sign off? Actual quantities will be pushed to each asset.")) return;
    setBusy(true);
    try {
      await post("/verification/sign-off", token, { verification_id: verificationId });
      toast.success("Signed off — asset stocks updated");
      onClose();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="inv-verify-sheet">
        <DialogHeader>
          <DialogTitle>Verification {verificationId} — {ver.center}</DialogTitle>
          <div className="text-xs text-muted-foreground">
            Scheduled {ver.scheduled_date} • Cadence {ver.cadence} • Status <b>{ver.status}</b>
            {ver.variance_total_value !== 0 && (
              <> • Variance <b className={ver.variance_total_value < 0 ? "text-red-500" : "text-green-600"}>
                {money(ver.variance_total_value)}</b></>
            )}
          </div>
        </DialogHeader>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Code</TableHead>
              <TableHead>Asset</TableHead>
              <TableHead className="text-right">Expected</TableHead>
              <TableHead className="text-right w-28">Actual</TableHead>
              <TableHead>Condition Seen</TableHead>
              <TableHead className="text-right">Variance</TableHead>
              <TableHead>Remarks</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {items.map((r, i) => (
                <TableRow key={r.asset_id}>
                  <TableCell className="font-mono text-xs">{r.asset_code}</TableCell>
                  <TableCell>{r.asset_name}</TableCell>
                  <TableCell className="text-right">{r.expected_qty} {r.unit}</TableCell>
                  <TableCell>
                    <Input type="number" className="w-24 text-right" value={r.actual_qty ?? ""}
                      disabled={readOnly}
                      onChange={(e) => setItem(i, "actual_qty", e.target.value)}
                      data-testid={`inv-verify-count-${r.asset_id}`} />
                  </TableCell>
                  <TableCell>
                    <Select value={r.condition_observed || r.expected_condition || "Good"}
                      disabled={readOnly}
                      onValueChange={(v) => setItem(i, "condition_observed", v)}>
                      <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        {conditions.map(c => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  </TableCell>
                  <TableCell className={`text-right ${(r.variance || 0) < 0 ? "text-red-500"
                                                   : (r.variance || 0) > 0 ? "text-green-600" : ""}`}>
                    {r.variance !== null && r.variance !== undefined ? r.variance : "—"}
                  </TableCell>
                  <TableCell>
                    <Input value={r.remarks || ""} disabled={readOnly}
                      onChange={(e) => setItem(i, "remarks", e.target.value)} />
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Close</Button>
          {!readOnly && (
            <Button onClick={submit} disabled={busy} data-testid="inv-verify-submit">
              {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Save Counts
            </Button>
          )}
          {isAdmin && ver.status === "Completed" && (
            <Button onClick={signOff} disabled={busy} data-testid="inv-verify-signoff">
              <CheckCircle2 className="w-4 h-4 mr-1" />Sign Off
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ============================================================
 * Movement / Transfer
 * ============================================================ */
export function MovementPanel({ token, center, centers, isAdmin }) {
  const [rows, setRows] = useState([]);
  const [assets, setAssets] = useState([]);
  const [creating, setCreating] = useState(false);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, a] = await Promise.all([
        post("/transfer/list", token, { center }),
        post("/assets/list", token, { center, limit: 500 }),
      ]);
      setRows(t.transfers || []);
      setAssets((a.assets || []).filter(x => !x.is_raw_material_link));
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [token, center]);
  useEffect(() => { load(); }, [load]);

  const act = async (t, action) => {
    if (!window.confirm(`${action.toUpperCase()} transfer ${t.transfer_id}?`)) return;
    try {
      await post(`/transfer/${action}`, token, { transfer_id: t.transfer_id });
      toast.success(`Transfer ${action}d`);
      load();
    } catch (e) { toast.error(e.message); }
  };

  return (
    <Card data-testid="inv-movement-panel">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <ArrowRightLeft className="w-4 h-4" /> Asset Movement / Transfer
          </CardTitle>
          <div className="text-xs text-muted-foreground">
            Manager requests a transfer to another centre — <b>Admin approval</b> is required
            before stock is moved.
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => setCreating(true)} data-testid="inv-transfer-new">
            <Plus className="w-4 h-4 mr-1" /> New Transfer
          </Button>
          <Button variant="ghost" size="sm" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader><TableRow>
            <TableHead>ID</TableHead>
            <TableHead>Asset</TableHead>
            <TableHead>From</TableHead>
            <TableHead>To</TableHead>
            <TableHead className="text-right">Qty</TableHead>
            <TableHead>Reason</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Requested</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map(t => (
              <TableRow key={t.transfer_id} data-testid={`inv-transfer-row-${t.transfer_id}`}>
                <TableCell className="font-mono text-xs">{t.transfer_id}</TableCell>
                <TableCell>{t.asset_code} — {t.asset_name}</TableCell>
                <TableCell>{t.from_center}</TableCell>
                <TableCell>{t.to_center}</TableCell>
                <TableCell className="text-right">{t.qty} {t.unit}</TableCell>
                <TableCell className="text-xs">{t.reason || "—"}</TableCell>
                <TableCell>
                  <Badge variant={t.status === "Approved" ? "default"
                                : t.status === "Rejected" ? "destructive" : "outline"}>
                    {t.status}
                  </Badge>
                </TableCell>
                <TableCell className="text-xs">{(t.requested_at || "").slice(0, 19)}<br />
                  <span className="text-muted-foreground">{t.requested_by}</span></TableCell>
                <TableCell className="text-right space-x-1">
                  {isAdmin && t.status === "Pending" && (
                    <>
                      <Button size="sm" variant="outline" onClick={() => act(t, "approve")}
                        data-testid={`inv-transfer-approve-${t.transfer_id}`}>
                        <CheckCircle2 className="w-4 h-4 mr-1" />Approve
                      </Button>
                      <Button size="sm" variant="ghost" onClick={() => act(t, "reject")}
                        data-testid={`inv-transfer-reject-${t.transfer_id}`}>
                        <XCircle className="w-4 h-4" />
                      </Button>
                    </>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={9} className="text-center text-muted-foreground py-6">
                No transfers.
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>

      {creating && <NewTransferDialog token={token} assets={assets}
        centers={centers} fromCenter={center}
        onClose={() => { setCreating(false); load(); }} />}
    </Card>
  );
}


function NewTransferDialog({ token, assets, centers, fromCenter, onClose }) {
  const [f, setF] = useState({ asset_id: "", to_center: "", qty: 1,
                                reason: "", remarks: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.asset_id) return toast.error("Select an asset");
    if (!f.to_center) return toast.error("Select destination centre");
    setBusy(true);
    try {
      await post("/transfer/create", token, f);
      toast.success("Transfer request submitted for admin approval");
      onClose();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  const destOptions = (centers.length ? centers : [{ code: "PB-HSR" }, { code: "PB-BLR" }])
    .filter(c => (c.code || c) !== fromCenter);
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="inv-transfer-new-dialog">
        <DialogHeader><DialogTitle>New Asset Transfer</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Asset *</Label>
            <Select value={f.asset_id} onValueChange={(v) => setF({ ...f, asset_id: v })}>
              <SelectTrigger data-testid="inv-transfer-asset"><SelectValue placeholder="Select asset" /></SelectTrigger>
              <SelectContent className="max-h-96">
                {assets.map(a => (
                  <SelectItem key={a.asset_id} value={a.asset_id}>
                    {a.asset_code} — {a.asset_name} (qty {a.quantity_current})
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div><Label>From</Label><Input value={fromCenter} disabled /></div>
          <div><Label>To Centre *</Label>
            <Select value={f.to_center} onValueChange={(v) => setF({ ...f, to_center: v })}>
              <SelectTrigger data-testid="inv-transfer-to"><SelectValue placeholder="Destination" /></SelectTrigger>
              <SelectContent>
                {destOptions.map(c => (
                  <SelectItem key={c.code || c} value={c.code || c}>{c.code || c}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Quantity *</Label>
            <Input type="number" value={f.qty} onChange={(e) => setF({ ...f, qty: Number(e.target.value) })}
              data-testid="inv-transfer-qty" /></div>
          <div className="col-span-2"><Label>Reason</Label>
            <Input value={f.reason} onChange={(e) => setF({ ...f, reason: e.target.value })} /></div>
          <div className="col-span-2"><Label>Remarks</Label>
            <Textarea rows={2} value={f.remarks} onChange={(e) => setF({ ...f, remarks: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="inv-transfer-submit">
            {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Submit for Approval
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


/* ============================================================
 * Repair & Maintenance
 * ============================================================ */
export function RepairPanel({ token, center }) {
  const [rows, setRows] = useState([]);
  const [assets, setAssets] = useState([]);
  const [creating, setCreating] = useState(false);
  const [completing, setCompleting] = useState(null);
  const [loading, setLoading] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [r, a] = await Promise.all([
        post("/repair/list", token, { center }),
        post("/assets/list", token, { center, limit: 500 }),
      ]);
      setRows(r.repairs || []);
      setAssets((a.assets || []).filter(x => !x.is_raw_material_link));
    } catch (e) { toast.error(e.message); }
    finally { setLoading(false); }
  }, [token, center]);
  useEffect(() => { load(); }, [load]);

  return (
    <Card data-testid="inv-repair-panel">
      <CardHeader className="flex flex-row items-center justify-between">
        <div>
          <CardTitle className="text-base flex items-center gap-2">
            <Wrench className="w-4 h-4" /> Repair & Maintenance
          </CardTitle>
          <div className="text-xs text-muted-foreground">
            Log a repair, track downtime and cost, and close with Repaired / Replaced / Scrapped outcome.
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={() => setCreating(true)} data-testid="inv-repair-new">
            <Plus className="w-4 h-4 mr-1" /> Log Repair
          </Button>
          <Button variant="ghost" size="sm" onClick={load} disabled={loading}>
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}
          </Button>
        </div>
      </CardHeader>
      <CardContent>
        <Table>
          <TableHeader><TableRow>
            <TableHead>ID</TableHead>
            <TableHead>Asset</TableHead>
            <TableHead>Vendor</TableHead>
            <TableHead>Issue</TableHead>
            <TableHead className="text-right">Est. Cost</TableHead>
            <TableHead className="text-right">Actual</TableHead>
            <TableHead>Downtime</TableHead>
            <TableHead>Status</TableHead>
            <TableHead>Outcome</TableHead>
            <TableHead className="text-right">Actions</TableHead>
          </TableRow></TableHeader>
          <TableBody>
            {rows.map(r => (
              <TableRow key={r.repair_id} data-testid={`inv-repair-row-${r.repair_id}`}>
                <TableCell className="font-mono text-xs">{r.repair_id}</TableCell>
                <TableCell>{r.asset_code} — {r.asset_name}</TableCell>
                <TableCell>{r.vendor || "—"}</TableCell>
                <TableCell className="text-xs max-w-[200px] truncate" title={r.issue}>{r.issue}</TableCell>
                <TableCell className="text-right">{money(r.cost_estimate)}</TableCell>
                <TableCell className="text-right">{r.cost_actual ? money(r.cost_actual) : "—"}</TableCell>
                <TableCell className="text-xs">
                  {(r.downtime_start || "").slice(0, 10)}
                  {r.downtime_end && ` → ${r.downtime_end.slice(0, 10)}`}
                </TableCell>
                <TableCell>
                  <Badge variant={r.status === "Completed" ? "default" : "secondary"}>
                    {r.status}
                  </Badge>
                </TableCell>
                <TableCell>{r.outcome || "—"}</TableCell>
                <TableCell className="text-right">
                  {r.status !== "Completed" && (
                    <Button size="sm" variant="outline" onClick={() => setCompleting(r)}
                      data-testid={`inv-repair-close-${r.repair_id}`}>Close</Button>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {!loading && rows.length === 0 && (
              <TableRow><TableCell colSpan={10} className="text-center text-muted-foreground py-6">
                No repairs logged.
              </TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </CardContent>

      {creating && <NewRepairDialog token={token} assets={assets}
        onClose={() => { setCreating(false); load(); }} />}
      {completing && <CompleteRepairDialog token={token} repair={completing}
        onClose={() => { setCompleting(null); load(); }} />}
    </Card>
  );
}


function NewRepairDialog({ token, assets, onClose }) {
  const [f, setF] = useState({ asset_id: "", vendor: "", issue: "",
                                cost_estimate: 0, downtime_start: "",
                                expected_return_date: "", remarks: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    if (!f.asset_id) return toast.error("Select an asset");
    if (!f.issue.trim()) return toast.error("Describe the issue");
    setBusy(true);
    try {
      await post("/repair/create", token, f);
      toast.success("Repair logged");
      onClose();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="inv-repair-new-dialog">
        <DialogHeader><DialogTitle>Log a Repair</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div className="col-span-2"><Label>Asset *</Label>
            <Select value={f.asset_id} onValueChange={(v) => setF({ ...f, asset_id: v })}>
              <SelectTrigger data-testid="inv-repair-asset"><SelectValue placeholder="Select asset" /></SelectTrigger>
              <SelectContent className="max-h-96">
                {assets.map(a => (
                  <SelectItem key={a.asset_id} value={a.asset_id}>
                    {a.asset_code} — {a.asset_name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Vendor</Label>
            <Input value={f.vendor} onChange={(e) => setF({ ...f, vendor: e.target.value })} /></div>
          <div><Label>Estimated Cost</Label>
            <Input type="number" value={f.cost_estimate}
              onChange={(e) => setF({ ...f, cost_estimate: Number(e.target.value) })} /></div>
          <div className="col-span-2"><Label>Issue *</Label>
            <Textarea rows={2} value={f.issue}
              onChange={(e) => setF({ ...f, issue: e.target.value })}
              data-testid="inv-repair-issue" /></div>
          <div><Label>Downtime Start</Label>
            <Input type="date" value={f.downtime_start}
              onChange={(e) => setF({ ...f, downtime_start: e.target.value })} /></div>
          <div><Label>Expected Return</Label>
            <Input type="date" value={f.expected_return_date}
              onChange={(e) => setF({ ...f, expected_return_date: e.target.value })} /></div>
          <div className="col-span-2"><Label>Remarks</Label>
            <Textarea rows={2} value={f.remarks}
              onChange={(e) => setF({ ...f, remarks: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="inv-repair-submit">
            {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Log Repair
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}


function CompleteRepairDialog({ token, repair, onClose }) {
  const [f, setF] = useState({ cost_actual: repair.cost_estimate || 0,
                                downtime_end: new Date().toISOString().slice(0, 10),
                                outcome: "Repaired", parts_replaced: "",
                                remarks: "" });
  const [busy, setBusy] = useState(false);
  const save = async () => {
    setBusy(true);
    try {
      await post("/repair/complete", token, { repair_id: repair.repair_id, ...f });
      toast.success("Repair closed");
      onClose();
    } catch (e) { toast.error(e.message); }
    finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="inv-repair-close-dialog">
        <DialogHeader><DialogTitle>Close Repair — {repair.asset_name}</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><Label>Outcome</Label>
            <Select value={f.outcome} onValueChange={(v) => setF({ ...f, outcome: v })}>
              <SelectTrigger data-testid="inv-repair-outcome"><SelectValue /></SelectTrigger>
              <SelectContent>
                {["Repaired", "Replaced", "Scrapped"].map(o =>
                  <SelectItem key={o} value={o}>{o}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div><Label>Actual Cost</Label>
            <Input type="number" value={f.cost_actual}
              onChange={(e) => setF({ ...f, cost_actual: Number(e.target.value) })}
              data-testid="inv-repair-actual-cost" /></div>
          <div><Label>Downtime End</Label>
            <Input type="date" value={f.downtime_end}
              onChange={(e) => setF({ ...f, downtime_end: e.target.value })} /></div>
          <div><Label>Parts Replaced</Label>
            <Input value={f.parts_replaced}
              onChange={(e) => setF({ ...f, parts_replaced: e.target.value })} /></div>
          <div className="col-span-2"><Label>Remarks</Label>
            <Textarea rows={2} value={f.remarks}
              onChange={(e) => setF({ ...f, remarks: e.target.value })} /></div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose} disabled={busy}>Cancel</Button>
          <Button onClick={save} disabled={busy} data-testid="inv-repair-complete">
            {busy && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}Complete
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
